import React, {
  FunctionComponent, useCallback, useMemo, useState,
} from 'react';
import { NextRouter, withRouter } from 'next/router';
import withNextConfig from '../../hocs/withNextConfig/withNextConfig';
import EvaluationForm, { EvaluationFormModel } from '../../components/evaluation-form/EvaluationForm';
import { getPlaceDetails } from '../../services/geo/geo';
import { GoogleMapsContext } from '../../contexts/google-map-context/GoogleMapsContext';
import getTrafficSource from '../../utils/traffic-source/getTrafficSource';
import { FormFieldsConfig } from '../../types';
import useAutoCompleteSessionToken from '../../hooks/useAutoCompleteSessionToken/useAutoCompleteSessionToken';

export const DEFAULT_EVALUATION_FORM_STORAGE_KEY = 'evaluation-form';

export interface CreateEvaluationProps {
  storageKey?: string,
  onSubmit?: VoidFunction,
  onError?: (error: Error) => void,
  router: NextRouter,
  config: { MAPS: { apiKey: string } },
  initialValues?: EvaluationFormModel,
  fields?: FormFieldsConfig,
  campaign?: string,
  dataTracking?: string,
}

export const CreateEvaluation: FunctionComponent<CreateEvaluationProps> = ({
  config,
  router,
  storageKey = DEFAULT_EVALUATION_FORM_STORAGE_KEY,
  initialValues,
  fields,
  campaign,
  dataTracking,
  onSubmit,
  onError,
}) => {
  const { apiKey } = config.MAPS;
  const [isSaving, setIsSaving] = useState(false);
  const { data: sessionToken } = useAutoCompleteSessionToken(apiKey);
  const googleMapsContext = useMemo(() => ({
    apiKey,
    sessionToken,
  }), [apiKey, sessionToken]);

  const saveValues = useCallback(async (values) => {
    let placeDetails = {};

    setIsSaving(true);

    try {
      placeDetails = await getPlaceDetails(apiKey, values.placeId, { sessionToken });
    } catch {
      placeDetails = {};
    }

    try {
      window.sessionStorage.setItem(storageKey, JSON.stringify({
        ...values,
        ...placeDetails,
        gclid: router.query.gclid,
        utmCampaign: router.query.utm_campaign || campaign,
        source: getTrafficSource(router.pathname?.startsWith('/landing-altroconsumo')
          ? { utm_source: 'altroconsumo' }
          : router.query),
        originUrl: window.location.href,
      }));

      if (onSubmit) {
        onSubmit();
      }
    } catch (error) {
      if (onError) {
        onError(error);
      }
    }

    setIsSaving(false);
  }, [router.query, router.pathname, storageKey, apiKey, sessionToken, campaign, onSubmit, onError]);

  return (
    <GoogleMapsContext.Provider
      value={googleMapsContext}
    >
      <EvaluationForm
        initialValues={initialValues}
        loading={!sessionToken || isSaving}
        fields={fields}
        dataTracking={dataTracking}
        onSubmit={saveValues}
      />
    </GoogleMapsContext.Provider>
  );
};

export default withNextConfig(withRouter(CreateEvaluation));
